import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useGetTrackByFrameSeqQuery, useUpdateTrackIDMutation } from '@/hook';
import { useFlag } from '@/package';
import {
  useAuthStore,
  useLabelingBoxStore,
  useTaskFrameStore,
  useTrackIDChangeRequestStore,
} from '@/store';
import type { BoxWithLabel, FrameData, TrackApi } from '@/type';

import type { EditLinkedTrackIDFormValue, LinkedFrameItemData } from './type';
import { isAssignedFrame } from './util';

type UseEditLinkedTrackIDSharedParam = {
  copiedTrackID: string;
  setValueShouldDirty?: boolean;
};

export const useEditLinkedTrackIDShared = ({
  copiedTrackID,
  setValueShouldDirty,
}: UseEditLinkedTrackIDSharedParam) => {
  const { mutateAsync: updateTrackID } = useUpdateTrackIDMutation();
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const pcdIndexBoxListMap = useLabelingBoxStore(
    state => state.pcdIndexBoxListMap,
  );
  const hasTrackIDChangeRequest = useTrackIDChangeRequestStore(state =>
    state.hasRequest(),
  );
  const linkedFrameListByRequest = useTrackIDChangeRequestStore(
    state => state.linkedFrameList,
  );
  const selectedBoxIdMap = useLabelingBoxStore(state => state.selectedBoxIdMap);
  const user = useAuthStore(state => state.user);
  const [excludedFrameSeqSet, setExcludedFrameSeqSet] = useState<Set<number>>(
    new Set(),
  );
  const boxList = useMemo(
    () => useLabelingBoxStore.getState().getCurrentBoxList(),
    [pcdIndexBoxListMap],
  );
  const selectedBox = useMemo(
    () =>
      useLabelingBoxStore
        .getState()
        .getCurrentSelectedBoxList()[0] as Nullish<BoxWithLabel>,
    [selectedBoxIdMap],
  );
  const { data: frameListByQuery } = useGetTrackByFrameSeqQuery({
    taskID: currentFrame?.taskID,
    trackID: hasTrackIDChangeRequest ? undefined : selectedBox?.trackId,
  });
  const linkedFrameList = useMemo(
    () =>
      hasTrackIDChangeRequest
        ? linkedFrameListByRequest
        : (frameListByQuery ?? []),
    [frameListByQuery, hasTrackIDChangeRequest, linkedFrameListByRequest],
  );
  const { formState, getValues, register, setValue } =
    useForm<EditLinkedTrackIDFormValue>({
      defaultValues: {
        copiedTrackID,
        currentTrackID: selectedBox?.trackId ?? '',
      },
    });
  const hasNotAssignedFrame = useMemo(() => {
    return linkedFrameList.some(frame => !isAssignedFrame(frame, user));
  }, [linkedFrameList, user]);
  const linkedFrameListGroup = useMemo(
    () =>
      linkedFrameList.reduce((acc, cur, index, list) => {
        const prev = list[index - 1];

        if (!prev || cur.frameSeq !== prev.frameSeq + 1) {
          acc.push([cur]);
        } else {
          acc[acc.length - 1].push(cur);
        }

        return acc;
      }, [] as LinkedFrameItemData[][]),
    [linkedFrameList],
  );
  const totalFrameCount = useMemo(
    () =>
      linkedFrameList.filter(frame => !excludedFrameSeqSet.has(frame.frameSeq))
        .length,
    [excludedFrameSeqSet, linkedFrameList],
  );
  const { setFalse: hide, setTrue: show, value: visible } = useFlag(false);

  const handleApply = (
    targetFrameList?: Pick<
      FrameData,
      'frameSeq' | 'reviewerID' | 'taskID' | 'workerID' | 'workState'
    >[],
  ) => {
    if (formState.errors.copiedTrackID || formState.errors.currentTrackID)
      return Promise.resolve();
    if (!currentFrame || !selectedBox?.trackId || !copiedTrackID) {
      return Promise.resolve();
    }

    const request: TrackApi.UpdateTrackID.Request = {
      newTrackID: copiedTrackID,
      taskID: currentFrame.taskID,
      trackID: selectedBox.trackId,
    };

    if (targetFrameList && targetFrameList.length > 0) {
      request.frameSeqRange = [
        `${targetFrameList[0].frameSeq}`,
        `${targetFrameList[targetFrameList.length - 1].frameSeq}`,
      ];
    }

    return updateTrackID(request).then(() => {
      useLabelingBoxStore
        .getState()
        .changeBoxTrackID(selectedBox.id, copiedTrackID);
    });
  };

  const handleRemoveItem = (frameItem: LinkedFrameItemData) => {
    setExcludedFrameSeqSet(prev => new Set([...prev, frameItem.frameSeq]));
  };

  const handleRemoveList = (targetFrameList: LinkedFrameItemData[]) => {
    setExcludedFrameSeqSet(prev => {
      const nextSet = new Set(prev);

      targetFrameList.forEach(frame => nextSet.add(frame.frameSeq));

      return nextSet;
    });
  };

  useEffect(() => {
    if (visible) {
      return;
    }

    setExcludedFrameSeqSet(new Set());
  }, [visible]);

  useEffect(() => {
    setValue('copiedTrackID', copiedTrackID, {
      shouldDirty: setValueShouldDirty,
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('currentTrackID', selectedBox?.trackId ?? '', {
      shouldDirty: setValueShouldDirty,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [copiedTrackID, selectedBox, setValue, setValueShouldDirty]);

  return {
    boxList,
    currentFrame,
    excludedFrameSeqSet,
    formState,
    getValues,
    handleApply,
    handleRemoveItem,
    handleRemoveList,
    hasNotAssignedFrame,
    hide,
    linkedFrameListGroup,
    pcdIndexBoxListMap,
    register,
    selectedBox,
    show,
    totalFrameCount,
    visible,
  };
};
